import { Component, computed, inject, OnInit, signal } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { forkJoin } from 'rxjs';

import {
  Banco,
  BancoIntegracion,
  BancoIntegracionRequest,
  BancoRequest,
} from '../../../core/models';
import { BancosService } from '../../../core/services/bancos.service';
import { DataTablePaginado } from '../../../shared/data-table-paginado/data-table-paginado';
import {
  TableAction,
  TableActionEvent,
  TableColumn,
  TablePageEvent,
} from '../../../shared/data-table-paginado/data-table-paginado.models';
import { PageHeaderComponent } from '../../../shared/page-header/page-header';
import { SummaryCardComponent } from '../../../shared/summary-card/summary-card';
import { UiButtonComponent } from '../../../shared/ui-button/ui-button';
import { ModalShellComponent } from '../../../shared/modal-shell/modal-shell';
import { ConfirmationModalComponent } from '../../../shared/confirmation-modal/confirmation-modal';
import { LoadingComponent } from '../../../shared/loading/loading';
import { MobileService } from '../../../core/services/mobile.service';
import { CardListComponent } from '../../../shared/card-list/card-list';
import { SearchInput } from '../../../shared/search-input/search-input';
import { BankIntegrationsPanelComponent } from './bank-integrations-panel/bank-integrations-panel';
import { SelectOption, SelectComponent } from '../../../shared/select/select';
import { FORM_LIMITS, isValidUrl } from '../../../core/utils/validation.utils';

type BankStatus = 'Activo' | 'Inactivo';
type IntegrationStatus = 'Activa' | 'Sin integración' | 'Inactiva' | 'En revisión';

interface BankRow extends Banco {
  integraciones: BancoIntegracion[];
  status: BankStatus;
  cuentasConectadas: number;
  ultimaSincronizacion: string | null;
  integrationStatus: IntegrationStatus;
}

@Component({
  selector: 'app-bancos',
  standalone: true,
  imports: [
    FormsModule,
    PageHeaderComponent,
    SummaryCardComponent,
    UiButtonComponent,
    ModalShellComponent,
    DataTablePaginado,
    ConfirmationModalComponent,
    LoadingComponent,
    CardListComponent,
    SearchInput,
    BankIntegrationsPanelComponent,
    SelectComponent,
  ],
  templateUrl: './bancos.html',
  styleUrl: './bancos.css',
})
export class BancosComponent implements OnInit {
  protected searchInput = '';
  protected search = '';
  protected statusFilter: BankStatus | 'Todos' = 'Activo';
  protected selectedBank: BancoIntegracion | null = null;
  protected showForm = false;
  protected showBankForm = false;
  protected testingId: string | null = null;
  protected saving = false;
  protected testMessage = signal('');
  protected formError = '';
  protected readonly rows = signal<BankRow[]>([]);
  protected readonly catalogBanks = signal<Banco[]>([]);
  protected readonly allIntegrations = signal<BancoIntegracion[]>([]);
  protected readonly totalItems = signal(0);
  protected page = signal(1);
  protected pageSize = signal(10);
  protected formBankId = '';
  protected formProvider = 'API';
  protected formUrl = '';
  protected formVersion = 'v1';
  protected formAuth = 'OAuth2';
  protected bankCode = '';
  protected bankName = '';
  protected bankLogo = '';
  protected editingBankId: string | null = null;
  protected readonly pageSizeOptions = [10, 25, 50, 100];
  protected showConfirmation = false;
  protected selectedBankForToggle: BankRow | null = null;
  protected loadingPage = true;
  protected loadingTable = false;
  protected readonly expandedBankId = signal<string | null>(null);
  protected readonly statusOptions: SelectOption[] = [
    { label: 'Todos', value: 'Todos' },
    { label: 'Activo', value: 'Activo' },
    { label: 'Inactivo', value: 'Inactivo' },
  ];
  protected readonly providerOptions: SelectOption[] = [
    { label: 'API bancaria', value: 'API' },
    { label: 'FTP', value: 'FTP' },
    { label: 'SFTP', value: 'SFTP' },
  ];
  protected readonly authOptions: SelectOption[] = [
    { label: 'OAuth 2.0', value: 'OAuth2' },
    { label: 'API Key', value: 'API Key' },
    { label: 'Basic', value: 'Basic' },
  ];

  private readonly bancosService = inject(BancosService);
  protected readonly mobileService = inject(MobileService);

  protected readonly activeCount = computed(
    () => this.catalogBanks().filter((bank) => bank.estaActivo).length,
  );
  protected readonly bankOptions = computed<SelectOption[]>(() =>
    this.catalogBanks().map((bank) => ({ label: bank.nombre, value: bank.id })),
  );
  protected readonly reviewCount = computed(
    () => this.allIntegrations().filter((item) => item.estadoUltimaPrueba === 'PENDIENTE').length,
  );
  protected readonly accountCount = computed(() =>
    this.allIntegrations().reduce((total, integration) => total + integration.cuentasConectadas, 0),
  );

  protected readonly columns: TableColumn<BankRow>[] = [
    {
      key: 'nombre',
      label: 'Banco',
      sortable: true,
      valueGetter: (row) => `${row.nombre} · ${row.codigo}`,
    },
    { key: 'status', label: 'Estado', type: 'badge', sortable: true, align: 'center' },
    {
      key: 'integrationStatus',
      label: 'Integración',
      type: 'badge',
      sortable: true,
      align: 'center',
      badgeClass: (value) =>
        'integration-' + this.integrationStatusClass(value as IntegrationStatus),
    },
    {
      key: 'integraciones',
      label: 'Integraciones',
      align: 'center',
      valueGetter: (row) => row.integraciones.length,
    },
    { key: 'cuentasConectadas', label: 'Cuentas', type: 'number', align: 'right', sortable: true },
    { key: 'ultimaSincronizacion', label: 'Última sincronización', type: 'datetime' },
  ];
  protected readonly actions: TableAction<BankRow>[] = [
    { id: 'edit', label: 'Editar banco', icon: 'fa-solid fa-pen' },
    { id: 'toggle', label: 'Cambiar estado', icon: 'fa-solid fa-toggle-on' },
  ];

  ngOnInit(): void {
    this.load();
  }

  private load(): void {
    this.loadingTable = true;
    forkJoin({
      page: this.bancosService.getPage({
        page: this.page(),
        pageSize: this.pageSize(),
        search: this.search,
        status: this.statusFilter,
      }),
      banks: this.bancosService.getAll(),
      integrations: this.bancosService.getAllIntegraciones(),
    }).subscribe({
      next: ({ page, banks, integrations }) => {
        this.catalogBanks.set(banks);
        this.allIntegrations.set(integrations);
        this.totalItems.set(page.data?.totalItems ?? 0);

        const grouped = new Map<string, BancoIntegracion[]>();
        integrations.forEach((integration) =>
          grouped.set(integration.bancoId, [
            ...(grouped.get(integration.bancoId) ?? []),
            integration,
          ]),
        );

        this.rows.set(
          (page.data?.items ?? []).map((bank) => {
            const items = grouped.get(bank.id) ?? [];
            const lastSync =
              items
                .map((item) => item.ultimaSincronizacion)
                .filter(Boolean)
                .sort()
                .at(-1) ?? null;
            const accounts = items.reduce((total, item) => total + item.cuentasConectadas, 0);
            const status: BankStatus = bank.estaActivo ? 'Activo' : 'Inactivo';
            const integrationStatus: IntegrationStatus = !items.length
              ? 'Sin integración'
              : items.some((item) => !item.estaActivo)
                ? 'Inactiva'
                : items.some((item) => item.estadoUltimaPrueba === 'PENDIENTE')
                  ? 'En revisión'
                  : 'Activa';
            return {
              ...bank,
              integraciones: items,
              cuentasConectadas: accounts,
              ultimaSincronizacion: lastSync,
              status,
              integrationStatus,
            };
          }),
        );
        this.loadingTable = false;
        this.loadingPage = false;
      },
      error: () => {
        this.rows.set([]);
        this.totalItems.set(0);
        this.loadingTable = false;
        this.loadingPage = false;
      },
    });
  }

  protected integrationStatusClass(status: IntegrationStatus): string {
    switch (status) {
      case 'Activa':
        return 'active';
      case 'Sin integración':
        return 'missing';
      case 'En revisión':
        return 'review';
      default:
        return 'inactive';
    }
  }

  protected onPageChange(event: TablePageEvent): void {
    this.page.set(event.page);
    this.pageSize.set(event.pageSize);
    this.load();
  }
  protected onPageSizeChange(event: TablePageEvent): void {
    this.page.set(1);
    this.pageSize.set(event.pageSize);
    this.load();
  }

  protected onSearch(): void {
    this.search = this.searchInput.trim();
    this.page.set(1);
    this.load();
  }

  protected onStatusChange(value: string | string[] | null): void {
    if (typeof value !== 'string') return;

    this.statusFilter = value as BankStatus | 'Todos';
    this.page.set(1);
    this.load();
  }
  protected onExpand(_event: { row: BankRow; expanded: boolean }): void {}

  protected isBankExpanded(bankId: string): boolean {
    return this.expandedBankId() === bankId;
  }

  protected toggleBankExpanded(bankId: string): void {
    this.expandedBankId.update((currentId) => (currentId === bankId ? null : bankId));
  }

  protected onAction(event: TableActionEvent<BankRow>): void {
    if (event.action === 'edit') this.editBank(event.row);
    if (event.action === 'toggle') {
      const bancoEncontrado = this.rows().find((bank) => bank.id === event.row.id);
      if (bancoEncontrado) {
        this.selectedBankForToggle = bancoEncontrado;
        this.showConfirmation = true;
      }
    }
  }

  protected getBadgeClass(status: BankStatus): string {
    return status === 'Activo' ? 'badge-active' : 'badge-inactive';
  }

  protected openNewBank(): void {
    this.formError = '';
    this.editingBankId = null;
    this.bankCode = '';
    this.bankName = '';
    this.bankLogo = '';
    this.showBankForm = true;
  }
  protected closeBankForm(): void {
    this.showBankForm = false;
  }
  protected saveBank(): void {
    this.bankCode = this.bankCode.trim().toUpperCase();
    this.bankName = this.bankName.trim();
    if (
      !this.bankName ||
      !/^[A-Z0-9_-]{2,30}$/.test(this.bankCode) ||
      this.bankName.length > FORM_LIMITS.nameMaxLength
    ) {
      this.formError = 'Ingresa un nombre y un código válidos para el banco.';
      return;
    }
    const request: BancoRequest = {
      codigo: this.bankCode,
      nombre: this.bankName,
      logoKey: this.bankLogo || null,
    };
    this.saving = true;
    const call = this.editingBankId
      ? this.bancosService.updateBank(this.editingBankId, request)
      : this.bancosService.createBank(request);
    call.subscribe({
      next: () => {
        this.saving = false;
        this.closeBankForm();
        this.load();
      },
      error: () => (this.saving = false),
    });
  }
  protected editBank(bank: BankRow): void {
    this.editingBankId = bank.id;
    this.bankCode = bank.codigo;
    this.bankName = bank.nombre;
    this.bankLogo = bank.logoKey ?? '';
    this.showBankForm = true;
  }

  protected openNew(bank?: BankRow | string): void {
    this.formError = '';
    this.selectedBank = null;
    this.formBankId =
      typeof bank === 'string' ? bank : (bank?.id ?? this.catalogBanks()[0]?.id ?? '');
    this.formProvider = 'API';
    this.formUrl = '';
    this.formVersion = 'v1';
    this.formAuth = 'OAuth2';
    this.showForm = true;
  }
  protected editIntegration(integration: BancoIntegracion): void {
    this.formError = '';
    this.selectedBank = integration;
    this.formBankId = integration.bancoId;
    this.formProvider = integration.tipoProveedor;
    this.formUrl = integration.urlBase;
    this.formVersion = integration.versionApi;
    this.formAuth = integration.tipoAutenticacion;
    this.showForm = true;
  }
  protected closeForm(): void {
    this.showForm = false;
    this.selectedBank = null;
  }
  protected save(): void {
    this.formUrl = this.formUrl.trim();
    this.formVersion = this.formVersion.trim();
    if (
      !this.formBankId ||
      !isValidUrl(this.formUrl) ||
      !this.formVersion ||
      this.formVersion.length > 30
    ) {
      this.formError = 'Ingresa una URL válida y una versión de API.';
      return;
    }
    const request: BancoIntegracionRequest = {
      tipoProveedor: this.formProvider,
      urlBase: this.formUrl,
      versionApi: this.formVersion,
      tipoAutenticacion: this.formAuth,
    };
    this.saving = true;
    const call = this.selectedBank
      ? this.bancosService.updateIntegration(this.formBankId, this.selectedBank.id, request)
      : this.bancosService.createIntegration(this.formBankId, request);
    call.subscribe({
      next: () => {
        this.saving = false;
        this.closeForm();
        this.load();
      },
      error: () => (this.saving = false),
    });
  }
  protected testConnection(integration: BancoIntegracion): void {
    this.testingId = integration.id;
    this.bancosService.testIntegration(integration.bancoId, integration.id).subscribe({
      next: () => {
        this.testingId = null;
        this.testMessage.set(`Conexión con ${integration.bancoNombre} verificada correctamente.`);
        this.load();
      },
      error: () => {
        this.testingId = null;
      },
    });
  }
  protected toggleIntegration(integration: BancoIntegracion): void {
    this.bancosService
      .toggleIntegration(integration.bancoId, integration.id, !integration.estaActivo)
      .subscribe(() => this.load());
  }

  protected toggleBank(bank: BankRow): void {
    this.bancosService.toggleBank(bank.id, !bank.estaActivo).subscribe(() => this.load());
  }

  protected confirmToggle(): void {
    if (!this.selectedBankForToggle) return;
    this.toggleBank(this.selectedBankForToggle);
    this.showConfirmation = false;
  }
  protected cancelToggle(): void {
    this.showConfirmation = false;
  }
}
